// contexts/GoogleDriveContext.jsx - REST API VERSION (Complete for HouseKeeping - Updated with Fixes for Reliable OAuth)
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { getFirebase } from '@services/firebase';
import { readGlobalProfiles } from '@services/firebaseGlobal';

const ACCESS_TOKEN_KEY = 'googleDriveAccessToken';
const REFRESH_TOKEN_KEY = 'googleDriveRefreshToken';
const TOKEN_EXPIRY_KEY = 'googleDriveTokenExpiry';
const LAST_AUTH_DATE_KEY = 'googleDriveLastAuthDate';

const getTodayDate = () => new Date().toISOString().split('T')[0];

const GoogleDriveContext = createContext();

export const useGoogleDrive = () => {
  const context = useContext(GoogleDriveContext);
  if (!context) {
    throw new Error('useGoogleDrive must be used within a GoogleDriveProvider');
  }
  return context;
};

// Helper functions
const isLocalDevelopment = () => {
  const isLocal = window.location.hostname === 'localhost' || 
         window.location.hostname === '127.0.0.1' ||
         window.location.port === '5173';
  return isLocal;
};

const getCallbackUrl = () => {
  const isLocal = isLocalDevelopment();
  
  if (isLocal) {
    return import.meta.env.VITE_CALLBACK_URL_LOCAL || 'http://localhost:5173/callback';
  }
  
  return import.meta.env.VITE_CALLBACK_URL || `${window.location.origin}/callback`;
};

const getBackendUrl = () => {
  const isLocal = isLocalDevelopment();
  
  if (isLocal && import.meta.env.VITE_BACKEND_URL_LOCAL) {
    return import.meta.env.VITE_BACKEND_URL_LOCAL;
  }
  
  return import.meta.env.VITE_BACKEND_URL || window.location.origin;
};

export const GoogleDriveProvider = ({ children }) => {
  // ✅ Get Firebase auth inside the component (lazy, safe)
  const { auth } = getFirebase();

  const [user] = useAuthState(auth);
  const [isDriveConnected, setIsDriveConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [googleSheets, setGoogleSheets] = useState([]);
  const [googleForms, setGoogleForms] = useState([]);

  const clearTokens = useCallback(() => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    setAccessToken(null);
    setIsDriveConnected(false);
    setGoogleSheets([]);
    setGoogleForms([]);
  }, []);

  // In GoogleDriveContext.jsx, add this function
  const clearTokensAndReconnect = useCallback(() => {
    clearTokens();
    setError('Permissions updated. Please reconnect to Google Drive.');
  }, [clearTokens]);

  // Constants - ADD SHEETS SCOPE
  const CLIENT_ID = import.meta.env.VITE_CLIENT_ID;
  const SCOPES ='https://www.googleapis.com/auth/drive https://www.googleapis.com/auth/forms.body https://www.googleapis.com/auth/spreadsheets' ;
  
  // Check if current user has drive access
  const hasDriveAccess = useCallback(() => {
    return userRole === 'Super' || userRole === 'Admin';
  }, [userRole]);

  // Token management functions
  const storeTokens = useCallback((accessToken, refreshToken, expiresIn) => {
    if (!hasDriveAccess()) return;
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    const expiryTime = Date.now() + (expiresIn * 1000);
    localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
    setAccessToken(accessToken);
  }, [hasDriveAccess]);

  const getAccessToken = () => hasDriveAccess() ? localStorage.getItem(ACCESS_TOKEN_KEY) : null;

  const isTokenValid = useCallback(() => {
    if (!hasDriveAccess()) return false;
    const token = getAccessToken();
    const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!token || !expiry) return false;
    return Date.now() < parseInt(expiry, 10);
  }, [hasDriveAccess]);

  // Fetch user role
  useEffect(() => {
    const fetchUserRole = async () => {
      if (!user) {
        setUserRole('');
        return;
      }
      try {
        const userList = await readGlobalProfiles('cUsers');
        const currentUserData = userList.find(userData => userData.id === user.uid);
        
        if (currentUserData) {
          let role = 'User';
          if (currentUserData.isadminuser) {
            role = 'Admin';
          } else if (currentUserData.issuperuser) {
            role = 'Super';
          } else if (currentUserData.ismover) {
            role = 'Mover';
          }
          setUserRole(role);
        } else {
          setUserRole('');
        }
      } catch (err) {
        console.error('Error fetching user role:', err);
        setUserRole('');
      }
    };
    fetchUserRole();
  }, [user]);

  // Refresh access token
  const refreshAccessToken = async () => {
    if (!hasDriveAccess()) return null;
    
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) return null;

    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        let errorBody = {};
        try { errorBody = await response.json(); } catch (_) {}

        const isInvalidGrant =
          errorBody?.error === 'invalid_grant' ||
          (typeof errorBody?.error_description === 'string' &&
            errorBody.error_description.toLowerCase().includes('invalid_grant'));

        // 404 = endpoint not found (permanent, not transient) — clear all tokens to stop retry loop
        // invalid_grant = token revoked/expired — clear all tokens
        // 4xx client errors — clear all tokens (bad request, our fault)
        // 5xx server errors — preserve refresh token, transient server issue
        const isServerError = response.status >= 500;

        if (isInvalidGrant || !isServerError) {
          console.error(`Token refresh failed with status ${response.status}. Clearing tokens.`);
          clearTokens();
        } else {
          console.error(`Token refresh server error (${response.status}). Preserving refresh token.`);
          localStorage.removeItem(ACCESS_TOKEN_KEY);
          localStorage.removeItem(TOKEN_EXPIRY_KEY);
          setAccessToken(null);
        }
        return null;
      }
      
      const data = await response.json();
      if (!data.access_token) throw new Error('No access token received');

      storeTokens(data.access_token, null, data.expires_in);
      return data.access_token;
    } catch (error) {
      // Network/fetch error — preserve refresh token so we can retry later
      console.error('Token refresh network error:', error);
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
      setAccessToken(null);
      return null;
    }
  };

  // Ensure valid token
  const ensureValidToken = async () => {
    try {
      let currentToken = getAccessToken();
      
      if (!currentToken) {
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        if (!refreshToken) return null;
        
        return await refreshAccessToken();
      }

      const tokenExpiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
      const isExpired = tokenExpiry && (Date.now() > (parseInt(tokenExpiry) - 300000));
      
      if (isExpired) {
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        if (!refreshToken) return null;
        
        return await refreshAccessToken();
      }
      
      return currentToken;
    } catch (error) {
      console.error('Token validation failed:', error);
      clearTokens();
      return null;
    }
  };

  // Safe auto-connect with sheet loading
  useEffect(() => {
    const safeAutoConnect = async () => {
      if (!hasDriveAccess()) return;

      const existingToken = getAccessToken();
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      
      if (!existingToken && !refreshToken) return;

      try {
        const token = await ensureValidToken();
        if (token) {
          setIsDriveConnected(true);
                   
          // Auto-fetch sheets and forms
          try {
            const sheetsResult = await fetchGoogleSheets();
            if (sheetsResult.success) {
              setGoogleSheets(sheetsResult.sheets || []);
            }
            
            const formsResult = await fetchGoogleForms();
            if (formsResult.success) {
              setGoogleForms(formsResult.forms || []);
            }
          } catch (fetchError) {
            console.error('Auto-fetch failed:', fetchError.message);
          }
        }
      } catch (error) {
        console.error('Auto-connect failed:', error.message);
      }
    };

    const timer = setTimeout(() => {
      safeAutoConnect();
    }, 2000);

    return () => clearTimeout(timer);
  }, [hasDriveAccess]);

  // ==============================
  // GOOGLE SHEETS FUNCTIONS (REST API)
  // ==============================

  // Fetch Google Sheets (REST API - NO gapi)
  const fetchGoogleSheets = async (searchTerm = '') => {
    if (!hasDriveAccess()) {
      throw new Error('User does not have drive access');
    }
    
    const token = await ensureValidToken();
    if (!token) throw new Error('No valid access token');

    try {
      const userEmail = user?.email || '';
      
      // Build query for Google Sheets files
      let query = "mimeType='application/vnd.google-apps.spreadsheet'";
      if (userEmail) {
        query += ` and '${userEmail}' in owners`;
      }
      if (searchTerm) {
        query += ` and name contains '${searchTerm}'`;
      }

      let allSheets = [];
      let pageToken = null;
      
      do {
        const url = `https://www.googleapis.com/drive/v3/files?` +
          `q=${encodeURIComponent(query)}&` +
          `fields=files(id,name,mimeType,createdTime,modifiedTime,owners),nextPageToken&` +
          `pageSize=100` +
          (pageToken ? `&pageToken=${pageToken}` : '');

        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });

        if (!response.ok) {
          if (response.status === 403) {
            clearTokensAndReconnect();
          }
          throw new Error(`HTTP ${response.status}: Failed to fetch sheets`);
        }

        const data = await response.json();
        allSheets = [...allSheets, ...(data.files || [])];
        pageToken = data.nextPageToken;
      } while (pageToken);
      return { success: true, sheets: allSheets };
    } catch (error) {
      console.error('Error fetching Google Sheets via REST:', error);
      return { success: false, error: error.message };
    }
  };

  // Fetch sheet metadata (REST API)
  const fetchSheetMetadata = async (sheetId) => {
    if (!hasDriveAccess()) throw new Error('User does not have drive access');
    
    const token = await ensureValidToken();
    if (!token) throw new Error('No valid access token');

    try {
      const response = await fetch(`https://sheets.googleapis.com/v4/spreadsheets/${sheetId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 403) {
          clearTokensAndReconnect();
        }
        throw new Error(`HTTP ${response.status}: Failed to fetch sheet metadata`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching sheet metadata:', error);
      throw new Error(`Failed to fetch sheet metadata: ${error.message}`);
    }
  };

  // Fetch data from Google Sheets (REST API)
  const fetchSheetData = async (sheetId, range) => {
    if (!hasDriveAccess()) throw new Error('User does not have drive access');
    
    const token = await ensureValidToken();
    if (!token) throw new Error('No valid access token');

    try {
      const url = `https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values/${encodeURIComponent(range)}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 403) {
          clearTokensAndReconnect();
        }
        throw new Error(`HTTP ${response.status}: Failed to fetch sheet data`);
      }

      const data = await response.json();
      return data.values || [];
    } catch (error) {
      console.error('Error fetching sheet data:', error);
      throw new Error(`Failed to fetch sheet data: ${error.message}`);
    }
  };

  // ==============================
  // GOOGLE FORMS FUNCTIONS (REST API)
  // ==============================

  // Fetch Google Forms (REST API)
  const fetchGoogleForms = async (searchTerm = '') => {
    if (!hasDriveAccess()) {
      throw new Error('User does not have drive access');
    }
    
    const token = await ensureValidToken();
    if (!token) throw new Error('No valid access token');

    try {
      const userEmail = user?.email || '';
      
      // First, get forms owned by the user
      let ownedQuery = "mimeType='application/vnd.google-apps.form'";
      if (userEmail) {
        ownedQuery += ` and '${userEmail}' in owners`;
      }
      if (searchTerm) {
        ownedQuery += ` and name contains '${searchTerm}'`;
      }

      let allForms = [];
      let pageToken = null;
      
      // Fetch owned forms
      do {
        const url = `https://www.googleapis.com/drive/v3/files?` +
          `q=${encodeURIComponent(ownedQuery)}&` +
          `fields=files(id,name,mimeType,createdTime,modifiedTime,owners),nextPageToken&` +
          `pageSize=100` +
          (pageToken ? `&pageToken=${pageToken}` : '');

        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });

        if (!response.ok) {
          if (response.status === 403) {
            clearTokensAndReconnect();
          }
          throw new Error(`HTTP ${response.status}: Failed to fetch forms`);
        }

        const data = await response.json();
        allForms = [...allForms, ...(data.files || [])];
        pageToken = data.nextPageToken;
      } while (pageToken);

      // Fetch shared forms
      let sharedQuery = "mimeType='application/vnd.google-apps.form' and sharedWithMe";
      if (searchTerm) {
        sharedQuery += ` and name contains '${searchTerm}'`;
      }

      pageToken = null;
      do {
        const url = `https://www.googleapis.com/drive/v3/files?` +
          `q=${encodeURIComponent(sharedQuery)}&` +
          `fields=files(id,name,mimeType,createdTime,modifiedTime,owners),nextPageToken&` +
          `pageSize=100` +
          (pageToken ? `&pageToken=${pageToken}` : '');

        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });

        if (!response.ok) {
          console.warn('Failed to fetch shared forms, continuing with owned only');
          break;
        }

        const data = await response.json();
        // Filter out duplicates
        data.files?.forEach(form => {
          if (!allForms.some(f => f.id === form.id)) {
            allForms.push(form);
          }
        });
        pageToken = data.nextPageToken;
      } while (pageToken);
      return { success: true, forms: allForms };
    } catch (error) {
      console.error('Error fetching Google Forms via REST:', error);
      return { success: false, error: error.message };
    }
  };

  // Fetch form responses (REST API)
  const fetchFormResponses = async (formId) => {
    if (!hasDriveAccess()) throw new Error('User does not have drive access');
    
    const token = await ensureValidToken();
    if (!token) throw new Error('No valid access token');

    try {
      const response = await fetch(`https://forms.googleapis.com/v1/forms/${formId}/responses`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        if (response.status === 403) {
          clearTokensAndReconnect();
        }
        throw new Error(`HTTP ${response.status}: Failed to fetch responses`);
      }
      
      const data = await response.json();
      return data.responses || [];
    } catch (error) {
      console.error('Error fetching form responses via REST:', error);
      throw new Error(`Failed to fetch form responses: ${error.message}`);
    }
  };

  // Fetch form details (REST API)
  const fetchFormDetails = async (formId) => {
    if (!hasDriveAccess()) throw new Error('User does not have drive access');
    
    const token = await ensureValidToken();
    if (!token) throw new Error('No valid access token');

    try {
      const response = await fetch(`https://forms.googleapis.com/v1/forms/${formId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        if (response.status === 403) {
          clearTokensAndReconnect();
        }
        throw new Error(`HTTP ${response.status}: Failed to fetch form details`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching form details via REST:', error);
      throw new Error(`Failed to fetch form details: ${error.message}`);
    }
  };

  // Delete entire Google Sheet
const deleteGoogleSheet = async (sheetId) => {
  if (!hasDriveAccess()) throw new Error('User does not have drive access');
  
  const token = await ensureValidToken();
  if (!token) throw new Error('No valid access token');

  try {
    const response = await fetch(`https://www.googleapis.com/drive/v3/files/${sheetId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Delete failed:', errorText);
      if (response.status === 403) {
        clearTokensAndReconnect();
      }
      throw new Error(`Failed to delete sheet: ${response.status} ${response.statusText}`);
    }
    
    // Remove from local state
    setGoogleSheets(prev => prev.filter(sheet => sheet.id !== sheetId));
    
    return { success: true };
  } catch (error) {
    console.error('Error deleting Google Sheet:', error);
    throw error;
  }
};


// Delete specific form response

const deleteFormResponse = async (formId, timestampData) => {
  if (!hasDriveAccess()) throw new Error('User does not have drive access');
  
  const token = await ensureValidToken();
  if (!token) throw new Error('No valid access token');

  try {
    const backendUrl = getBackendUrl();
    
    // Handle both old (string) and new (object) formats for backward compatibility
    let requestBody;
    if (typeof timestampData === 'string') {
      // Old format: timestampData is just createTime string
      requestBody = { 
        formId, 
        timestampData: {
          timestamp: timestampData,
          createTime: timestampData,
          lastSubmittedTime: null
        }
      };
    } else {
      // New format: timestampData is an object
      requestBody = { 
        formId, 
        timestampData 
      };
    }
    
    const response = await fetch(`${backendUrl}/api/delete-form-response-proxy`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Delete failed with response:', errorText);
      let errorMessage = `Proxy Error ${response.status}`;
      
      try {
        const errorData = JSON.parse(errorText);
        errorMessage += `: ${errorData.error || 'Unknown error'}`;
      } catch (e) {
        errorMessage += `: ${errorText}`;
      }
      
      throw new Error(errorMessage);
    }
    
    const result = await response.json();
    
    return result;
    
  } catch (error) {
    console.error('Error deleting form response:', error);
    throw error;
  }
};

// Delete specific rows from Google Sheet
const deleteSheetRows = async (spreadsheetId, sheetName, rowNumbers) => {
  if (!hasDriveAccess()) throw new Error('User does not have drive access');
  
  const token = await ensureValidToken();
  if (!token) throw new Error('No valid access token');

  try {
    // First, get sheet metadata to find the sheetId
    const metadata = await fetchSheetMetadata(spreadsheetId);
    const sheet = metadata.sheets.find(s => s.properties.title === sheetName);
    
    if (!sheet) {
      throw new Error(`Sheet "${sheetName}" not found`);
    }
    
    const sheetId = sheet.properties.sheetId;
    
    // Sort rows in descending order to avoid index shifting
    const sortedRows = [...rowNumbers].sort((a, b) => b - a);
    
    // Create batch update requests
    const requests = sortedRows.map(rowNumber => ({
      deleteDimension: {
        range: {
          sheetId: sheetId,
          dimension: "ROWS",
          startIndex: rowNumber - 1, // Convert to 0-indexed
          endIndex: rowNumber
        }
      }
    }));
    
    const response = await fetch(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}:batchUpdate`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ requests }),
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json();
      if (response.status === 403) {
        clearTokensAndReconnect();
      }
      throw new Error(errorData.error?.message || 'Failed to delete sheet rows');
    }
    
    return { success: true, deletedRows: rowNumbers.length };
  } catch (error) {
    console.error('Error deleting sheet rows:', error);
    throw error;
  }
};

// Clear all data from a sheet (without deleting the sheet)
const clearSheetData = async (spreadsheetId, sheetName) => {
  if (!hasDriveAccess()) throw new Error('User does not have drive access');
  
  const token = await ensureValidToken();
  if (!token) throw new Error('No valid access token');

  try {
    // Get metadata to find the sheet
    const metadata = await fetchSheetMetadata(spreadsheetId);
    const sheet = metadata.sheets.find(s => s.properties.title === sheetName);
    
    if (!sheet) {
      throw new Error(`Sheet "${sheetName}" not found`);
    }
    
    // Clear the entire sheet
    const response = await fetch(
      `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${sheetName}:clear`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json();
      if (response.status === 403) {
        clearTokensAndReconnect();
      }
      throw new Error(errorData.error?.message || 'Failed to clear sheet data');
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error clearing sheet data:', error);
    throw error;
  }
};

  // ==============================
  // CONNECTION MANAGEMENT
  // ==============================

  // Connect to Google Drive
  const connectToGoogleDrive = async () => {
    if (!hasDriveAccess()) {
      setError('Access denied. Only Admin and Super users can connect.');
      return { success: false, error: 'Access denied' };
    }

    setIsLoading(true);
    setError(null);

    try {
      // Check if we already have a valid token
      const token = await ensureValidToken();
      if (token) {
        setIsDriveConnected(true);
        
        // Fetch sheets and forms after connection
        try {
          const sheetsResult = await fetchGoogleSheets();
          if (sheetsResult.success) {
            setGoogleSheets(sheetsResult.sheets || []);
          }
          else {
            console.error('Failed to fetch sheets:', sheetsResult.error);
            setError(`Failed to fetch Google Sheets: ${sheetsResult.error}`);
          }
          const formsResult = await fetchGoogleForms();
          if (formsResult.success) {
            setGoogleForms(formsResult.forms || []);
          } else {
            console.error('Failed to fetch forms:', formsResult.error);
            setError(`Failed to fetch Google Forms: ${formsResult.error}`);
          }
        } catch (fetchError) {
          console.error('Fetch error:', fetchError.message);
          setError(`Failed to fetch data: ${fetchError.message}`);
        }
        
        setIsLoading(false);
        return { success: true, alreadyConnected: true };
      }

      // ensureValidToken() returned null — access token expired AND auto-refresh failed.
      // Before forcing a full OAuth redirect, try a direct silent refresh one more time
      // as long as we still have a refresh token. This handles the case where the access
      // token expired but the refresh token is still valid (can happen days/weeks after
      // last full auth — the date check is irrelevant here).
      const hasRefreshToken = !!localStorage.getItem(REFRESH_TOKEN_KEY);

      if (hasRefreshToken) {
        const silentToken = await refreshAccessToken();
        if (silentToken) {
          setIsDriveConnected(true);
          try {
            const sheetsResult = await fetchGoogleSheets();
            if (sheetsResult.success) setGoogleSheets(sheetsResult.sheets || []);
            const formsResult = await fetchGoogleForms();
            if (formsResult.success) setGoogleForms(formsResult.forms || []);
          } catch (fetchError) {
            console.error('Fetch after silent refresh failed:', fetchError.message);
          }
          setIsLoading(false);
          return { success: true, silentRefresh: true };
        }
        // Refresh token still present means it was a transient network/server error —
        // don't force OAuth, just report a connectivity problem.
        const refreshTokenStillPresent = !!localStorage.getItem(REFRESH_TOKEN_KEY);
        if (refreshTokenStillPresent) {
          setIsLoading(false);
          setError('Could not reach Google to refresh your session. Please check your connection and try again.');
          return { success: false, error: 'Silent refresh failed, refresh token preserved' };
        }
        // Refresh token was cleared (invalid_grant / revoked) — must do full OAuth
      }

      // Generate OAuth state
      const state = JSON.stringify({
        timestamp: Date.now(),
        userId: user?.uid || 'unknown',
        redirectTo: window.location.pathname
      });

      // Get callback URL
      const callbackUrl = getCallbackUrl();

      // No refresh token — need full OAuth with consent to get a new refresh token
      const prompt = 'consent';

      // Build OAuth URL
      const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${encodeURIComponent(CLIENT_ID)}&` +
        `redirect_uri=${encodeURIComponent(callbackUrl)}&` +
        `response_type=code&` +
        `scope=${encodeURIComponent(SCOPES)}&` +
        `access_type=offline&` +
        `prompt=${encodeURIComponent(prompt)}&` +
        `state=${encodeURIComponent(state)}`;

      // Store state
      localStorage.setItem('oauth_state', state);
      
      // Redirect
      window.location.href = authUrl;
      
      return { success: true, redirecting: true };
    } catch (error) {
      setIsLoading(false);
      console.error('OAuth initialization error:', error);
      setError(`Failed to start Google OAuth: ${error.message}`);
      return { success: false, error: error.message };
    }
  };

  // Process OAuth redirect
  const processOAuthRedirect = async (code) => {
    if (!hasDriveAccess()) {
      return { success: false, error: 'Access denied' };
    }

    try {
      setIsLoading(true);
      const backendUrl = getBackendUrl();
      const callbackUrl = getCallbackUrl();
      const response = await fetch(`${backendUrl}/exchange-token`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Origin': window.location.origin
        },
        body: JSON.stringify({ 
          code, 
          redirect_uri: callbackUrl 
        }),
      });
      
      if (!response.ok) {
        let errorMessage = `Token exchange failed: ${response.status}`;
        
        if (response.status === 429) {
          errorMessage = 'Too many authentication attempts. Please wait 15 minutes and try again.';
        } else if (response.status === 400) {
          errorMessage = 'Invalid authorization code. Please try authenticating again.';
        }
        
        throw new Error(errorMessage);
      }
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Token exchange failed');
      }

      const { access_token, refresh_token, expires_in } = result;
      storeTokens(access_token, refresh_token, expires_in);
      // Record the date of this full OAuth authentication
      localStorage.setItem(LAST_AUTH_DATE_KEY, getTodayDate());
      setIsDriveConnected(true);
      
      return { success: true };
    } catch (error) {
      console.error('OAuth redirect processing failed:', error);
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  // Disconnect Google Drive
  const disconnectGoogleDrive = () => clearTokens();

  // Auto-connect on app startup
  useEffect(() => {
    const autoConnect = async () => {
      if (!hasDriveAccess()) return;
      
      try {
        const token = await ensureValidToken();
        if (token) {
          setIsDriveConnected(true);
          
          // Optionally fetch sheets and forms on startup
          if (googleSheets.length === 0) {
            try {
              const sheetsResult = await fetchGoogleSheets();
              if (sheetsResult.success) {
                setGoogleSheets(sheetsResult.sheets || []);
              }
            } catch (error) {
              console.error('Startup sheet fetch failed:', error.message);
            }
          }
          
          if (googleForms.length === 0) {
            try {
              const formsResult = await fetchGoogleForms();
              if (formsResult.success) {
                setGoogleForms(formsResult.forms || []);
              }
            } catch (error) {
              console.error('Startup form fetch failed:', error.message);
            }
          }
        }
      } catch (error) {
        console.error('Auto-connect not possible:', error.message);
      }
    };
    
    autoConnect();
  }, [hasDriveAccess]);

  const value = {
    // Connection state
    isDriveConnected,
    isLoading,
    error,
    accessToken,
    userRole,
    googleSheets,
    googleForms,
    hasDriveAccess,
    
    // Connection management
    connectToGoogleDrive,
    disconnectGoogleDrive,
    ensureValidToken,
    processOAuthRedirect,
    isTokenValid,

    // Token management helper
    clearTokensAndReconnect, // ← ADD THIS
    
    // Google Sheets functions (for HouseKeeping)
    fetchSheetData,
    fetchSheetMetadata,
    fetchGoogleSheets,
    
    // Google Forms functions (for Enquiry)
    fetchFormResponses,
    fetchFormDetails,
    fetchGoogleForms,

    // Delete functions
    deleteGoogleSheet,
    deleteFormResponse,
    deleteSheetRows,
    clearSheetData
  };

  return (
    <GoogleDriveContext.Provider value={value}>
      {children}
    </GoogleDriveContext.Provider>
  );
};